import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Email as EmailIcon,
  Business as OrgIcon,
  CalendarToday as DateIcon,
  CheckCircle as EnabledIcon,
  Block as DisabledIcon,
  FileDownload as ExportIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminApi, UserDetails, Job } from '../services/api';

const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<UserDetails | null>(null);
  const [usageHistory, setUsageHistory] = useState<any[]>([]);
  const [usageByService, setUsageByService] = useState<any[]>([]);
  const [recentJobs, setRecentJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportPeriod, setExportPeriod] = useState<string>('3');
  const [exporting, setExporting] = useState(false);
  const [resending, setResending] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    organizationId: '',
    isPaid: false,
    maxRunsPerMonth: 50,
    maxDocChatLimit: 20,
    maxResearchLimit: 20,
    firstName: '',
    lastName: '',
    embeddings: false,
  });
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Helper function to format milliseconds to human-readable duration
  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      const remainingSeconds = seconds % 60;
      return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Status helper functions
  const getStatusChipProps = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return { label: 'Confirmed', color: 'success' as const };
      case 'FORCE_CHANGE_PASSWORD':
        return { label: 'Invited - Pending Login', color: 'warning' as const };
      case 'UNCONFIRMED':
        return { label: 'Pending Verification', color: 'warning' as const };
      case 'ARCHIVED':
        return { label: 'Archived', color: 'default' as const };
      case 'COMPROMISED':
        return { label: 'Compromised', color: 'error' as const };
      case 'UNKNOWN':
        return { label: 'Unknown', color: 'default' as const };
      case 'RESET_REQUIRED':
        return { label: 'Reset Required', color: 'warning' as const };
      default:
        return { label: status, color: 'default' as const };
    }
  };

  const handleResendInvitation = async () => {
    if (!userId) return;
    try {
      setResending(true);
      setError(null);
      setSuccessMessage(null);
      await adminApi.resendUserInvitation(userId);
      setSuccessMessage('Invitation email resent successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to resend invitation');
    } finally {
      setResending(false);
    }
  };

  const handleResendVerification = async () => {
    if (!userId) return;
    try {
      setResending(true);
      setError(null);
      setSuccessMessage(null);
      await adminApi.resendUserVerification(userId);
      setSuccessMessage('Verification code resent successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to resend verification code');
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const response = await adminApi.getUser(userId);
        
        if (response.success) {
          setUser(response.data.user);
          setUsageHistory(response.data.usage?.history || []);
          setUsageByService(response.data.usage?.byService || []);
          setRecentJobs(response.data.recentJobs || []);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load user details');
      } finally {
        setLoading(false);
      }
    };

    const fetchOrganizations = async () => {
      try {
        const response = await adminApi.listOrganizations();
        if (response.success) {
          setOrganizations(response.data.organizations.map((org: any) => ({
            // CRITICAL FIX: API returns 'id', not 'organizationId'
            // Using org.name as fallback was storing NAME instead of ID!
            id: org.id || org.organizationId || org.name,
            name: org.name || org.displayName || org.id
          })));
        }
      } catch (err) {
        console.warn('Failed to fetch organizations:', err);
      }
    };

    fetchUserDetails();
    fetchOrganizations();
  }, [userId]);

  const handleOpenEditDialog = () => {
    if (user) {
      setEditForm({
        organizationId: user.organizationId || 'default-org',
        isPaid: user.isPaid,
        maxRunsPerMonth: user.maxRunsPerMonth || 50,
        maxDocChatLimit: user.maxDocChatLimit || 20,
        maxResearchLimit: user.maxResearchLimit || 20,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        embeddings: user.embeddingsEnabled,
      });
      setEditDialogOpen(true);
    }
  };

  const handleSaveUser = async () => {
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      // backend update-user now writes custom:organizationId (previously a
      // no-op). Only send it when the admin actually changed it, and treat the
      // 'default-org' sentinel as "no org" (empty string = unassign) so an
      // unrelated save never stamps the sentinel onto an org-less user.
      const loadedOrg = user.organizationId && user.organizationId !== 'default-org' ? user.organizationId : '';
      const formOrg = editForm.organizationId && editForm.organizationId !== 'default-org' ? editForm.organizationId : '';
      const updates: Parameters<typeof adminApi.updateUser>[1] = {
        isPaid: editForm.isPaid,
        maxRunsPerMonth: editForm.maxRunsPerMonth,
        maxDocChatLimit: editForm.maxDocChatLimit,
        maxResearchLimit: editForm.maxResearchLimit,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        embeddings: editForm.embeddings,
      };
      if (formOrg !== loadedOrg) {
        updates.organizationId = formOrg;
      }

      await adminApi.updateUser(user.userId, updates);

      // Refresh user data
      const response = await adminApi.getUser(user.userId);
      if (response.success) {
        setUser(response.data.user);
      }

      setEditDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;

    try {
      if (user.isEnabled) {
        await adminApi.disableUser(user.userId);
      } else {
        await adminApi.enableUser(user.userId);
      }
      // Refresh user data
      const response = await adminApi.getUser(user.userId);
      if (response.success) {
        setUser(response.data.user);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update user status');
    }
  };

  const handleExportUser = async (format: 'excel' | 'pdf') => {
    if (!user) return;
    
    try {
      setExporting(true);
      const months = parseInt(exportPeriod);
      
      // Use the per-user job history export endpoint
      const response = await adminApi.exportUserJobs(user.userId, {
        months,
        format
      });
      
      if (response.success && response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
        
        // Show warning if results were truncated
        if (response.data.truncated && response.data.warning) {
          setError(`⚠️ ${response.data.warning}. Try using a shorter time period for complete results.`);
        }
      }
      
      setExportDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to export user job history');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!user) return;

    try {
      setDeleting(true);
      setError(null);
      
      await adminApi.deleteUser(user.userId);
      
      // Navigate back to users list on success
      navigate('/users');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete user');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/users')} sx={{ mb: 2 }}>
          Back to Users
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/users')} sx={{ mb: 2 }}>
          Back to Users
        </Button>
        <Alert severity="warning">User not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/users')} sx={{ mb: 3 }}>
        Back to Users
      </Button>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* User Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {user.firstName || user.lastName 
                  ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                  : user.email
                }
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <Typography color="text.secondary">{user.email}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={user.isEnabled ? <EnabledIcon /> : <DisabledIcon />}
                label={user.isEnabled ? 'Active' : 'Disabled'}
                color={user.isEnabled ? 'success' : 'error'}
              />
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleOpenEditDialog}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={() => setExportDialogOpen(true)}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                color={user.isEnabled ? 'error' : 'success'}
                onClick={handleToggleStatus}
              >
                {user.isEnabled ? 'Disable User' : 'Enable User'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete User
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Organization</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <OrgIcon fontSize="small" color="action" />
                <Typography>{user.organizationId || 'default-org'}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip 
                  size="small" 
                  {...getStatusChipProps(user.status)} 
                />
                {user.status === 'FORCE_CHANGE_PASSWORD' && (
                  <Button 
                    size="small" 
                    variant="text" 
                    onClick={handleResendInvitation}
                    disabled={resending}
                    sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    {resending ? 'Resending...' : 'Resend Invitation'}
                  </Button>
                )}
                {user.status === 'UNCONFIRMED' && (
                  <Button 
                    size="small" 
                    variant="text" 
                    onClick={handleResendVerification}
                    disabled={resending}
                    sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    {resending ? 'Resending...' : 'Resend Code'}
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Created</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateIcon fontSize="small" color="action" />
                <Typography>{new Date(user.createdAt).toLocaleDateString()}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Monthly Limit</Typography>
              <Typography>{user.maxRunsPerMonth || '50'} runs</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Doc Chat Limit</Typography>
              <Typography>{user.maxDocChatLimit || '20'} runs</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Research Limit</Typography>
              <Typography>{user.maxResearchLimit || '20'} runs</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Paid User</Typography>
              <Chip label={user.isPaid ? 'Yes' : 'No'} size="small" color={user.isPaid ? 'success' : 'default'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>MFA Enabled</Typography>
              <Chip label={user.mfaEnabled ? 'Yes' : 'No'} size="small" color={user.mfaEnabled ? 'primary' : 'default'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Embeddings</Typography>
              <Chip label={user.embeddingsEnabled ? 'Enabled' : 'Disabled'} size="small" color={user.embeddingsEnabled ? 'primary' : 'default'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Groups</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {user.groups.length > 0 
                  ? user.groups.map(g => <Chip key={g} label={g} size="small" />)
                  : <Typography variant="body2" color="text.secondary">None</Typography>
                }
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Usage Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Usage History (Last 3 Months)
              </Typography>
              <Box sx={{ height: 250 }}>
                {usageHistory.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[...usageHistory].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="completedCount" name="Completed" fill="#38a169" />
                      <Bar dataKey="failedCount" name="Failed" fill="#e53e3e" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No usage data</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Usage by Service
              </Typography>
              {usageByService.length > 0 ? (
                <Box>
                  {usageByService.slice(0, 8).map((item, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        py: 1,
                        borderBottom: index < usageByService.length - 1 ? 1 : 0,
                        borderColor: 'divider'
                      }}
                    >
                      <Typography variant="body2">{item.service}</Typography>
                      <Chip label={item.count} size="small" />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">No service data</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Jobs */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Recent Jobs
          </Typography>
          {recentJobs.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job ID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Processing Time</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Cost ($)</TableCell>
                    <TableCell align="right">Input Tokens</TableCell>
                    <TableCell align="right">Output Tokens</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentJobs.map((job) => (
                    <TableRow key={job.jobId}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {job.jobId.substring(0, 8)}...
                      </TableCell>
                      <TableCell>{job.jobType}</TableCell>
                      <TableCell>
                        <Chip 
                          label={job.status} 
                          size="small" 
                          color={
                            job.status === 'COMPLETED' ? 'success' :
                            job.status === 'FAILED' ? 'error' :
                            job.status === 'PROCESSING' ? 'warning' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {job.processingTimeMs ? formatDuration(job.processingTimeMs) : '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {job.tokenUsage?.model || '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>
                        {job.tokenUsage?.costUsd !== undefined ? `$${job.tokenUsage.costUsd.toFixed(4)}` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {job.tokenUsage?.inputTokens ? job.tokenUsage.inputTokens.toLocaleString() : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {job.tokenUsage?.outputTokens ? job.tokenUsage.outputTokens.toLocaleString() : '-'}
                      </TableCell>
                      <TableCell>{job.progress}%</TableCell>
                      <TableCell>{new Date(job.createdAt).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">No recent jobs</Typography>
          )}
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Export User Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Export usage history for {user.firstName || user.email}
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={exportPeriod}
              label="Time Period"
              onChange={(e) => setExportPeriod(e.target.value)}
            >
              <MenuItem value="1">Last 1 Month</MenuItem>
              <MenuItem value="2">Last 2 Months</MenuItem>
              <MenuItem value="3">Last 3 Months</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="outlined" 
            onClick={() => handleExportUser('pdf')}
            disabled={exporting}
          >
            {exporting ? <CircularProgress size={20} /> : 'PDF'}
          </Button>
          <Button 
            variant="contained" 
            onClick={() => handleExportUser('excel')}
            disabled={exporting}
          >
            {exporting ? <CircularProgress size={20} /> : 'Excel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={editForm.organizationId}
                  label="Organization"
                  onChange={(e) => setEditForm({ ...editForm, organizationId: e.target.value })}
                >
                  <MenuItem value="default-org">default-org</MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Monthly Run Limit"
                value={editForm.maxRunsPerMonth}
                onChange={(e) => setEditForm({ ...editForm, maxRunsPerMonth: parseInt(e.target.value) || 50 })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Doc Chat Limit"
                value={editForm.maxDocChatLimit}
                onChange={(e) => setEditForm({ ...editForm, maxDocChatLimit: parseInt(e.target.value) || 20 })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Research Limit"
                value={editForm.maxResearchLimit}
                onChange={(e) => setEditForm({ ...editForm, maxResearchLimit: parseInt(e.target.value) || 20 })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isPaid}
                    onChange={(e) => setEditForm({ ...editForm, isPaid: e.target.checked })}
                    color="success"
                  />
                }
                label="Paid User"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.embeddings}
                    onChange={(e) => setEditForm({ ...editForm, embeddings: e.target.checked })}
                    color="primary"
                  />
                }
                label="Embeddings Enabled"
              />
            </Grid>
          </Grid>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveUser}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete User Account</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Warning: This action cannot be undone!</strong>
            <br />
            This will permanently delete the user account from both Cognito and the database.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To confirm deletion, please type the user's email address below:
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
            {user?.email}
          </Typography>
          <TextField
            fullWidth
            label="Confirm Email Address"
            value={deleteConfirmEmail}
            onChange={(e) => setDeleteConfirmEmail(e.target.value)}
            placeholder={user?.email}
            disabled={deleting}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteUser}
            disabled={deleting || deleteConfirmEmail.toLowerCase() !== user?.email.toLowerCase()}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetailPage;

